import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Bot, User, Sparkles, RefreshCw } from 'lucide-react';
import { useNirchalAI } from '../../hooks/useNirchalAI';

interface NirchalAIAssistantProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NirchalAIAssistant: React.FC<NirchalAIAssistantProps> = ({
  isOpen,
  onClose
}) => {
  const { messages, isTyping, sendMessage, clearChat } = useNirchalAI();
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus input when assistant opens and lock body scroll
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (inputMessage.trim()) {
      await sendMessage(inputMessage.trim());
      setInputMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = async (suggestion: string) => {
    await sendMessage(suggestion);
  };

  const formatMessageContent = (content: string) => {
    // Replace **text** with bold formatting
    const boldFormatted = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Replace bullet points with proper HTML
    const bulletFormatted = boldFormatted.replace(/^• /gm, '<div class="flex items-start gap-2 mb-1"><span class="text-pink-500 mt-1">•</span><span>').replace(/\n• /g, '</span></div><div class="flex items-start gap-2 mb-1"><span class="text-pink-500 mt-1">•</span><span>') + (boldFormatted.includes('• ') ? '</span></div>' : '');
    
    // Replace numbered lists
    const numberedFormatted = bulletFormatted.replace(/^(\d+)\. (.*?)$/gm, '<div class="flex items-start gap-2 mb-2"><span class="text-pink-600 font-medium">$1.</span><span>$2</span></div>');
    
    return numberedFormatted.split('\n').map((line, index) => (
      <div key={index} dangerouslySetInnerHTML={{ __html: line }} className={line.trim() === '' ? 'mb-2' : ''} />
    ));
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[10200] bg-black bg-opacity-50 flex items-end sm:items-end sm:justify-end"
      onClick={onClose}
    >
      {/* AI Assistant Chat Window */}
      <div 
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[92vh] sm:h-[600px] sm:m-4 flex flex-col overflow-hidden border-t border-gray-200 sm:border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile Drag Handle */}
        <div className="sm:hidden bg-gradient-to-r from-pink-500 to-purple-600 pt-3 pb-1 flex justify-center">
          <div className="w-12 h-1 bg-white bg-opacity-40 rounded-full"></div>
        </div>
        
        {/* Header - Extra padding-top on mobile for browser chrome */}
        <div className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-3 pt-3 pb-3 sm:px-4 sm:py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="relative flex-shrink-0">
              <Bot className="w-7 h-7 sm:w-8 sm:h-8" />
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 absolute -top-1 -right-1 text-yellow-300" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-base sm:text-lg truncate">Nirchal AI</h3>
              <p className="text-xs sm:text-sm opacity-90 truncate">Your Shopping Assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2">
            <button
              onClick={clearChat}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors flex-shrink-0 hidden sm:flex"
              title="Clear Chat"
              aria-label="Clear Chat"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="p-2.5 sm:p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full sm:rounded-lg transition-colors flex-shrink-0 shadow-lg"
              title="Close Assistant"
              aria-label="Close Assistant"
            >
              <X className="w-6 h-6 sm:w-5 sm:h-5 font-bold" strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {messages.map((message: any) => (
            <div key={message.id} className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              {message.type === 'assistant' && (
                <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Bot className="w-5 h-5 text-white" />
                </div>
              )}
              
              <div className={`max-w-[80%] ${message.type === 'user' ? 'order-2' : ''}`}>
                <div
                  className={`rounded-2xl p-4 ${
                    message.type === 'user'
                      ? 'bg-pink-500 text-white ml-auto'
                      : 'bg-white border border-gray-200 shadow-sm'
                  }`}
                >
                  <div className={`${message.type === 'user' ? 'text-white' : 'text-gray-800'} leading-relaxed`}>
                    {message.type === 'user' ? (
                      message.content
                    ) : (
                      formatMessageContent(message.content)
                    )}
                  </div>
                </div>

                {/* Suggestions */}
                {message.type === 'assistant' && message.suggestions && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {message.suggestions.map((suggestion: string, index: number) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="px-3 py-2 text-sm bg-pink-50 text-pink-600 rounded-full border border-pink-200 hover:bg-pink-100 transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}

                <div className="mt-1 text-xs text-gray-500">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>

              {message.type === 'user' && (
                <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1 order-3">
                  <User className="w-5 h-5 text-white" />
                </div>
              )}
            </div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex gap-3 justify-start">
              <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  <span className="text-gray-500 text-sm ml-2">Nirchal AI is thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me about products, sizes, prices..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                disabled={isTyping}
              />
            </div>
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isTyping}
              className="p-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:from-pink-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <div className="mt-2 text-xs text-gray-500 text-center">
            Powered by Nirchal AI • Real-time product data
          </div>
        </div>
      </div>
    </div>
  );
};